// useCreateTask.js

import { useMutation } from "@apollo/client";
import { CREATE_TASK } from "../../graphql/mutations/ProjectEditContext";

/**
 * Custom hook to create a task using a GraphQL mutation.
 *
 * @returns {[Function, Object]} A function to create the task and an object containing data, error, and loading states.
 */
const useCreateTask = () => {
  const [mutation, { data, error, loading }] = useMutation(CREATE_TASK);

  /**
   * Function to create a task with the provided parameters.
   *
   * @param {CreateTaskParams} params - The task creation parameters.
   * @returns {CreateTaskResult|undefined} The result of the mutation.
   *
   * 1. If the mutation fails due to network or server issues, `undefined` is returned.
   * 2. If the mutation is executed but rejected by the server (e.g., task creation failed),
   *    a `CreateTaskResult` object with `success: false` and an `errorResult` is returned.
   */
  const createTask = async ({ projectId, parentTaskId, insertAfterTaskId, task }) => {
    const variables = {
      createTaskParams: {
        projectId,
        parentTaskId,
        insertAfterTaskId,
      },
      task,
    };

    const res = await mutation({ variables });

    // Return result object or undefined if the mutation fails (e.g., network error).
    return res?.data?.projects?.createTask;
  };

  return [createTask, { data, error, loading }];
};

/**
 * @typedef {Object} CreateTaskParams
 * @property {number} projectId - The project ID.
 * @property {number} [parentTaskId] - The parent task ID, if applicable.
 * @property {number} [insertAfterTaskId] - The task ID to insert after, if applicable.
 * @property {Object} task - The task object with the data to be created.
 */

/**
 * @typedef {Object} CreateTaskResult
 * @property {boolean} success - Indicates whether the task creation was successful.
 * @property {Object|null} errorResult - Present only if the creation failed. Contains details of the error.
 * @property {Object|undefined} task - Present only if the creation was successful. Contains the created task.
 */

/**
 * Exporting the custom hook for external usage.
 */
export { useCreateTask };
